import React, { useState, useEffect } from "react";
import {
	Switch,
	Tooltip,
	Button,
	TablePagination,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import TableComponent from "./Table";
import config from "../config.json"; // Adjust the path as per your project structure
import axios from "axios";
import { useUser } from "../contexts/UserContext";

const PlanTable = () => {
	const { userId } = useUser();
	const [plans, setPlans] = useState([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedPlan, setSelectedPlan] = useState(null);
	const [openCloneDialog, setOpenCloneDialog] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [successMessage, setSuccessMessage] = useState(null);
	const navigate = useNavigate();

	console.log("User ID from context:", userId);

	useEffect(() => {
		if (!userId) {
			handleLogout();
		}
	}, [userId, navigate]);

	const handleLogout = () => {
		localStorage.removeItem("authToken");
		localStorage.removeItem("userData");
		navigate("/login");
	};

	useEffect(() => {
		const fetchPlans = async () => {
			try {
				if (!userId) return; // Ensure userId is defined before fetching plans

				console.log("User ID:", userId);
				const response = await fetch(
					`${config.baseURL}middlewarePlans`,
					{
						headers: {
							...config.headers,
						},
					}
				);
				const data = await response.json();

				console.log("Fetched plans:", data);

				// Filter plans where the semicolon-separated user_id contains the current userId
				const userIdStr = String(userId);
				const filteredPlans = data.filter((plan) => {
					if (!plan.user_id) return false;
					const ids = String(plan.user_id).split(";").map((id) => id.trim());
					return ids.includes(userIdStr);
				});

				console.log("Filtered plans for user:", filteredPlans);

				setPlans(filteredPlans.reverse()); // Ensure latest created data is displayed first
			} catch (error) {
				console.error("Error fetching plans:", error);
			}
		};

		fetchPlans();
	}, []);

	const handleEdit = (row) => {
		navigate(`/connection-details?planId=${row.id}&mode=edit`);
	};

	const handleDelete = async (row) => {
		try {
			const response = await fetch(
				`${config.baseURL}middlewarePlans/${row.id}`,
				{
					method: "DELETE",
					headers: {
						...config.headers,
					},
				}
			);
			if (response.ok) {
				const updatedPlans = plans.filter((plan) => plan.id !== row.id);
				setPlans(updatedPlans);
				// alert(`Deleted plan: ${row.plan_name}`);
			} else {
				console.error("Failed to delete the plan:", response.status);
				alert("Error deleting the plan. Please try again.");
			}
		} catch (error) {
			console.error("Error deleting the plan:", error);
			alert("Error deleting the plan. Please try again.");
		}
	};

	const confirmDelete = (row) => {
		setSelectedPlan(row);
		setOpenDialog(true);
	};

	const handleDialogClose = (confirm) => {
		setOpenDialog(false);
		if (confirm && selectedPlan) {
			handleDelete(selectedPlan);
		}
		setSelectedPlan(null);
	};

	const handleStatusChange = async (row) => {
		const newStatus = row.status === "active" ? "inactive" : "active";

		try {
			const response = await fetch(
				`${config.baseURL}middlewarePlans/${row.id}`,
				{
					method: "PATCH",
					headers: {
						...config.headers,
					},
					body: JSON.stringify({ status: newStatus }),
				}
			);

			if (response.ok) {
				const updatedPlans = plans.map((plan) =>
					plan.id === row.id ? { ...plan, status: newStatus } : plan
				);
				setPlans(updatedPlans);
			} else {
				console.error("Failed to update status:", response.status);
				alert("Error updating status. Please try again.");
			}
		} catch (error) {
			console.error("Error updating status:", error);
			alert("Error updating status. Please try again.");
		}
	};

	const confirmClone = (row) => {
		setSelectedPlan(row);
		setOpenCloneDialog(true);
	};

	const handleCloneDialogClose = (confirm) => {
		setOpenCloneDialog(false);
		if (confirm && selectedPlan) {
			handleClonePlan(selectedPlan.id);
		}
		setSelectedPlan(null);
	};

	const handleClonePlan = async (planId) => {
		setLoading(true);
		setError(null);
		const currentTimestamp = new Date().toISOString();

		try {
			// Fetch existing plan details
			const planResponse = await axios.get(
				`${config.baseURL}middlewarePlans/${planId}`,
				{
					headers: {
						...config.headers,
					},
				}
			);

			const planData = planResponse.data[0];

			// Create new plan with fetched details
			const newPlanName = `${planData.plan_name} - ${Math.floor(
				1000 + Math.random() * 9000
			)}`;

			console.log("newPlanName", newPlanName);
			const newPlanResponse = await axios.post(
				`${config.baseURL}middlewarePlans`,
				{
					plan_name: newPlanName,
					source: planData.source,
					target: planData.target,
					status: planData.status,
					user_id: String(planData.user_id),
				},
				{
					headers: {
						...config.headers,
					},
				}
			);

			const newPlanId = newPlanResponse.data.upsertedId;
			console.log("newPlanId", newPlanId);

			// Fetch existing connections
			const connectionsResponse = await axios.get(
				`${config.baseURL}middlewarePlanconnections?query=plan_id:${planId}`,
				{
					headers: {
						...config.headers,
					},
				}
			);

			const connectionsData = connectionsResponse.data;
			console.log("connectionsData", connectionsData);

			// Create new connections for the new plan
			for (const connection of connectionsData) {
				await axios.post(
					`${config.baseURL}middlewarePlanconnections`,
					{
						plan_id: newPlanId,
						source_uri: connection.source_uri,
						target_uri: connection.target_uri,
						source_api_token: connection.source_api_token,
						target_api_token: connection.target_api_token,
						other_target_details: connection.other_target_details,
						created_at: currentTimestamp,
						updated_at: currentTimestamp,
					},
					{
						headers: {
							...config.headers,
						},
					}
				);
			}

			// Fetch existing field mappings
			const mappingsResponse = await axios.get(
				`${config.baseURL}middlewarePlanmappings?query=plan_id:${planId}`,
				{
					headers: {
						...config.headers,
					},
				}
			);

			const mappingsData = mappingsResponse.data;

			// Create new field mappings for the new plan
			for (const mapping of mappingsData) {
				await axios.post(
					`${config.baseURL}middlewarePlanmappings`,
					{
						plan_id: newPlanId,
						plan_mapping_category_id: mapping.plan_mapping_category_id,
						plan_mapping_category_name: mapping.plan_mapping_category_name,
						fieldMappings: mapping.fieldMappings,
						created_at: currentTimestamp,
						updated_at: currentTimestamp,
					},
					{
						headers: {
							...config.headers,
						},
					}
				);
			}

			// Update the state with the new plan
			const newPlan = {
				id: newPlanId,
				plan_name: newPlanName,
				source: planData.source,
				target: planData.target,
				status: planData.status,
			};
			setPlans((prevPlans) => [newPlan, ...prevPlans]);

			setSuccessMessage("Plan cloned successfully!");
			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (error) {
			console.error("Error cloning plan:", error);
			setError("Failed to clone plan. Please try again later.");
		} finally {
			setLoading(false);
		}
	};

	const displayedPlans = plans.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage
	);

	const columns = [
		{ header: "Plan Name", field: "plan_name" },
		{ header: "Source", field: "source" },
		{ header: "Target", field: "target" },
		{
			header: "Status",
			field: "status",
			render: (row) => {
				return (
					<Switch
						checked={row.status === "active"}
						onChange={() => handleStatusChange(row)}
						color="default"
						sx={{
							"& .MuiSwitch-thumb": {
								backgroundColor: row.status === "active" ? "blue" : undefined,
							},
						}}
					/>
				);
			},
		},
		// {
		//   header: "Transactions",
		//   field: "transactions_count",
		//   render: (row) => (
		//     <>
		//       {row.transactions_count}
		//       <Tooltip title="View Transactions">
		//         <IconButton onClick={() => navigate("/transaction-flow")}>
		//           <OpenInNewIcon />
		//         </IconButton>
		//       </Tooltip>
		//     </>
		//   ),
		// }
	];

	const actions = [
		{
			icon: (
				<Tooltip title="Edit Plan">
					<EditIcon sx={{ color: "black" }} />
				</Tooltip>
			),
			handler: handleEdit,
		},
		{
			icon: (
				<Tooltip title="Clone Plan">
					<ContentCopyIcon sx={{ color: "black" }} />
				</Tooltip>
			),
			handler: confirmClone,
		},
		{
			icon: (
				<Tooltip title="Delete Plan">
					<DeleteIcon sx={{ color: "#FF0000" }} />
				</Tooltip>
			),
			handler: confirmDelete,
		},
	];

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleCreateNewPlan = () => {
		navigate("/connection-details");
	};

	return (
		<div>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					marginBottom: "20px",
				}}
			>
				<Button
					variant="contained"
					sx={{
						backgroundColor: "#FF7600",
						color: "#2c2d2d",
						"&:hover": {
							backgroundColor: "#e26a00",
						},
					}}
					onClick={handleCreateNewPlan}
				>
					<strong>+ Create New Plan</strong>
				</Button>
			</div>
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}

			{loading && <p>Loading...</p>}

			<TableComponent
				rows={displayedPlans}
				columns={columns}
				actions={actions}
				onActionClick={(row, action) => action.handler(row)}
			/>
			<TablePagination
				component="div"
				count={plans.length}
				page={page}
				onPageChange={handleChangePage}
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				rowsPerPageOptions={[5, 10, 25, 50, 100]}
				labelDisplayedRows={({ from, to, count }) =>
					`${from}-${to} of ${count}`
				}
			/>

			<Dialog
				open={openCloneDialog}
				onClose={() => handleCloneDialogClose(false)}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">{`Clone Plan: ${selectedPlan?.plan_name}`}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						Are you sure you want to clone this plan?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleCloneDialogClose(false)} color="primary">
						No
					</Button>
					<Button
						onClick={() => handleCloneDialogClose(true)}
						color="primary"
						autoFocus
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={openDialog}
				onClose={() => handleDialogClose(false)}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">{`Delete Plan: ${selectedPlan?.plan_name}`}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						Are you sure you want to delete this plan?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleDialogClose(false)} color="primary">
						No
					</Button>
					<Button
						onClick={() => handleDialogClose(true)}
						color="primary"
						autoFocus
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default PlanTable;
